/**
 * girlcave — soft page changes while the playlist plays.
 *
 * User request 2026-09-24: the music must not stop when a visitor clicks
 * through to a product. Every link used to be a full page load, and a page
 * load tears down the hidden Spotify embed (assets/gc-player.js). Resuming
 * on the next page is no answer: a fresh embed needs a new user gesture to
 * play (autoplay policy) and restarts with a gap.
 *
 * So once the visitor has pressed a player pill, this script takes over
 * same-site link clicks: it fetches the next page, swaps everything AROUND
 * the two player cards, and pushes the new URL. The cards are NEVER
 * detached — moving an iframe in the DOM reloads it, which would cut the
 * music just the same. The swap walks the fixed shell (layout/theme.liquid)
 * level by level and replaces every child except the one that leads to a
 * player (LEVELS below):
 *
 *   #MainContent       keeps .gc-container
 *   .gc-container      keeps .gc-section-main, .gc-player-mobile and the
 *                      inline sidebar-zoom script (it keeps observing)
 *   .gc-section-main   keeps .gc-sidebar — everything else is the page
 *   .gc-sidebar        keeps .gc-player — the menu card is replaced, so the
 *                      active pill follows the page
 *
 * Gated on the player: until the first pill press nothing is intercepted,
 * so a visitor who never plays music gets plain page loads (full Shopify
 * analytics, zero risk). After that, anything that does not fit the swap
 * falls back to a normal page load, and the music stops as it did before:
 * - HARD_PATHS (cart, checkout, account, search, policies, …) and files;
 * - a fetched page without the shell, or whose cart-drawer presence differs
 *   (/cart renders no drawer — layout/theme.liquid);
 * - fetch/parse errors, non-HTML responses, the theme editor.
 *
 * What a page load did implicitly and the swap redoes:
 * - <title>, meta description, canonical; new head stylesheets.
 * - The page's own stylesheets are loaded BEFORE the swap through temporary
 *   head copies (removed once the page's own <link>s have loaded), so the
 *   page never paints unstyled and no page CSS outlives its page.
 * - DOMParser scripts never run: new external scripts are re-created once
 *   per URL (Dawn's and the gc custom elements guard their define(), and
 *   elements upgrade on insert), inline ones re-run.
 * - gc scripts that scan the DOM once (gc-scrollbar.js,
 *   gc-sync-animations.js, gc-backdrop.js) re-scan on the `gc:page-load`
 *   event dispatched after every swap.
 * - The drop page's own backdrop plates (gc-backdrop.liquid): plate <style>
 *   and data attributes are carried over.
 * - The top tile is replaced, so the cart icon is re-bound to the drawer.
 * - Standard events: a PageViewEvent, as on DOMContentLoaded
 *   (layout/theme.liquid). Shopify's built-in analytics counts only real
 *   page loads, so a listener's later page views do not reach it.
 * - Scroll: top on a new page, the saved position on back/forward.
 */
(function () {
  'use strict';

  if (!window.fetch || !window.DOMParser || !window.history || !history.pushState) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var main = document.getElementById('MainContent');
  if (!main || !main.querySelector('.gc-section-main')) return;

  var LEVELS = [
    { parent: '#MainContent', keep: '.gc-container' },
    { parent: '.gc-container', keep: '.gc-section-main, .gc-player-mobile, script:not([src])' },
    { parent: '.gc-section-main', keep: '.gc-sidebar' },
    { parent: '.gc-sidebar', keep: '.gc-player' },
  ];

  /* Never soft: pages outside the shell, or ones that must be a real load
     (checkout hand-off, account forms). An optional locale prefix
     (/en/cart) is allowed for. */
  var HARD_PATHS =
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart|checkouts?|account|password|admin|apps|services|tools|policies|search|challenge|localization)(?:[/.]|$)/i;
  var FILE_PATH = /\.[a-z0-9]{2,5}$/i;

  var HOVER_DELAY_MS = 90; /* hover intent before a prefetch */
  var PREFETCH_TTL_MS = 10000;
  var STYLE_WAIT_MS = 3000; /* a stylesheet that hangs must not block the page */

  var visitId = 0;
  var pageHref = stripHash(location.href); /* the page the DOM currently shows */
  var softened = false; /* true once this document pushed its first entry */
  var prefetched = Object.create(null);
  var loadedScripts = Object.create(null);

  Array.prototype.forEach.call(document.scripts, function (script) {
    if (script.src) loadedScripts[script.src] = true;
  });

  function stripHash(href) {
    var i = href.indexOf('#');
    return i === -1 ? href : href.slice(0, i);
  }

  function playerLive() {
    return !!document.querySelector(
      '.gc-player[data-gc-player-mounted], .gc-player[data-gc-player-requested]'
    );
  }

  function softUrl(link) {
    if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) return null;
    var target = link.getAttribute('target');
    if (target && target !== '_self') return null;
    var url;
    try {
      url = new URL(link.href, location.href);
    } catch (error) {
      return null;
    }
    if (url.origin !== location.origin) return null;
    if (HARD_PATHS.test(url.pathname) || FILE_PATH.test(url.pathname)) return null;
    /* In-page anchors stay native. */
    if (url.hash && stripHash(url.href) === stripHash(location.href)) return null;
    return url;
  }

  function timeout(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function whenLoaded(link) {
    return new Promise(function (resolve) {
      if (link.sheet) {
        resolve();
        return;
      }
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
    });
  }

  /* --- fetching ---------------------------------------------------------- */

  function request(href) {
    return fetch(href, { credentials: 'same-origin', headers: { Accept: 'text/html' } }).then(
      function (res) {
        var type = res.headers.get('content-type') || '';
        /* 404 is a shell template (gc-404), so it swaps like any page. */
        if ((!res.ok && res.status !== 404) || type.indexOf('text/html') === -1) {
          throw new Error('gc-navigation: not a page');
        }
        return res.text().then(function (html) {
          return { url: res.url || href, html: html };
        });
      }
    );
  }

  function fetchPage(href) {
    var hit = prefetched[href];
    delete prefetched[href];
    if (hit && Date.now() - hit.at < PREFETCH_TTL_MS) return hit.promise;
    return request(href);
  }

  function prefetch(link) {
    if (!playerLive()) return;
    var url = softUrl(link);
    if (!url) return;
    var href = stripHash(url.href);
    if (href === pageHref || prefetched[href]) return;
    var promise = request(href);
    prefetched[href] = { at: Date.now(), promise: promise };
    promise.catch(function () {
      if (prefetched[href] && prefetched[href].promise === promise) delete prefetched[href];
    });
  }

  /* --- planning: validate everything BEFORE the first mutation ------------ */

  function keepers(parent, selector) {
    return Array.prototype.filter.call(parent.children, function (el) {
      return el.matches(selector);
    });
  }

  function planSwap(doc) {
    var newMain = doc.getElementById('MainContent');
    if (!newMain) return null;
    if (!document.querySelector('cart-drawer') !== !doc.querySelector('cart-drawer')) return null;

    var levels = [];
    for (var i = 0; i < LEVELS.length; i++) {
      var oldParent = i === 0 ? main : main.querySelector(LEVELS[i].parent);
      var newParent = i === 0 ? newMain : newMain.querySelector(LEVELS[i].parent);
      if (!oldParent || !newParent) return null;
      /* Each level's parent must be a keeper of the level above it. */
      if (i > 0) {
        if (levels[i - 1].oldKeep.indexOf(oldParent) === -1) return null;
        if (levels[i - 1].newKeep.indexOf(newParent) === -1) return null;
      }
      var oldKeep = keepers(oldParent, LEVELS[i].keep);
      var newKeep = keepers(newParent, LEVELS[i].keep);
      if (!oldKeep.length || oldKeep.length !== newKeep.length) return null;
      for (var j = 0; j < oldKeep.length; j++) {
        if (
          oldKeep[j].tagName !== newKeep[j].tagName ||
          oldKeep[j].classList[0] !== newKeep[j].classList[0]
        ) {
          return null;
        }
      }
      levels.push({ oldParent: oldParent, newParent: newParent, oldKeep: oldKeep, newKeep: newKeep });
    }

    var backdrop = planBackdrop(doc);
    if (backdrop === false) return null;
    return { levels: levels, backdrop: backdrop };
  }

  /* Per-page plates (drop page). null = nothing to do, false = cannot sync. */
  function planBackdrop(doc) {
    var oldEl = document.querySelector('.gc-backdrop');
    var newEl = doc.querySelector('.gc-backdrop');
    if (!oldEl || !newEl) return oldEl || newEl ? false : null;
    if (
      oldEl.getAttribute('data-gc-plate-desktop') === newEl.getAttribute('data-gc-plate-desktop') &&
      oldEl.getAttribute('data-gc-plate-mobile') === newEl.getAttribute('data-gc-plate-mobile')
    ) {
      return null;
    }
    var oldStyle = oldEl.previousElementSibling;
    var newStyle = newEl.previousElementSibling;
    if (!oldStyle || !newStyle || oldStyle.tagName !== 'STYLE' || newStyle.tagName !== 'STYLE') {
      return false;
    }
    return { oldEl: oldEl, newEl: newEl, oldStyle: oldStyle, newStyle: newStyle };
  }

  /* --- preparing: stylesheets + plate, while the old page still shows ----- */

  function prepare(doc, plan) {
    var inHead = Object.create(null);
    Array.prototype.forEach.call(document.head.querySelectorAll('link[rel="stylesheet"]'), function (link) {
      if (!link.hasAttribute('data-gc-nav-bridge')) inHead[link.href] = true;
    });

    var waits = [];
    var bridges = [];

    /* New head stylesheets stay for good, as on a real load. */
    Array.prototype.forEach.call(doc.head.querySelectorAll('link[rel="stylesheet"]'), function (link) {
      if (!link.href || inHead[link.href]) return;
      inHead[link.href] = true;
      var fresh = document.createElement('link');
      fresh.rel = 'stylesheet';
      fresh.href = link.href;
      document.head.appendChild(fresh);
      waits.push(whenLoaded(fresh));
    });

    /* The page's own stylesheets get a temporary head copy — also when the
       old page used the same file, because its <link> leaves with it. */
    Array.prototype.forEach.call(
      doc.getElementById('MainContent').querySelectorAll('link[rel="stylesheet"]'),
      function (link) {
        if (!link.href || inHead[link.href]) return;
        inHead[link.href] = true;
        var bridge = document.createElement('link');
        bridge.rel = 'stylesheet';
        bridge.href = link.href;
        bridge.setAttribute('data-gc-nav-bridge', '');
        document.head.appendChild(bridge);
        bridges.push(bridge);
        waits.push(whenLoaded(bridge));
      }
    );

    if (plan.backdrop) {
      var desktop = window.matchMedia('(min-width: 768px)').matches;
      var plate = plan.backdrop.newEl.getAttribute(desktop ? 'data-gc-plate-desktop' : 'data-gc-plate-mobile');
      if (plate) {
        waits.push(
          new Promise(function (resolve) {
            var img = new Image();
            img.onload = img.onerror = resolve;
            img.src = plate;
          })
        );
      }
    }

    return Promise.race([Promise.all(waits), timeout(STYLE_WAIT_MS)]).then(function () {
      return bridges;
    });
  }

  /* --- committing: one synchronous pass, so nothing paints half-swapped --- */

  function syncAttributes(oldEl, newEl) {
    /* style is JS-owned on these nodes (--gc-sidebar-zoom). */
    Array.prototype.slice.call(oldEl.attributes).forEach(function (attr) {
      if (attr.name !== 'style' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    });
    Array.prototype.forEach.call(newEl.attributes, function (attr) {
      if (attr.name !== 'style' && oldEl.getAttribute(attr.name) !== attr.value) {
        oldEl.setAttribute(attr.name, attr.value);
      }
    });
  }

  /* Replace every child except the keepers. Keepers never move; the new
     nodes are inserted in front of the keeper they precede in the new
     markup, so the order matches the fetched page. */
  function replaceAround(level, inserted) {
    Array.prototype.slice.call(level.oldParent.childNodes).forEach(function (node) {
      if (level.oldKeep.indexOf(node) === -1) level.oldParent.removeChild(node);
    });
    var next = 0;
    Array.prototype.slice.call(level.newParent.childNodes).forEach(function (node) {
      var k = level.newKeep.indexOf(node);
      if (k !== -1) {
        next = k + 1;
        return;
      }
      level.oldParent.insertBefore(node, level.oldKeep[next] || null);
      if (node.nodeType === 1) inserted.push(node);
    });
  }

  function syncHead(doc) {
    document.title = doc.title;
    ['meta[name="description"]', 'link[rel="canonical"]'].forEach(function (selector) {
      var oldEl = document.head.querySelector(selector);
      var newEl = doc.head.querySelector(selector);
      if (oldEl && newEl) oldEl.replaceWith(newEl);
      else if (oldEl) oldEl.remove();
      else if (newEl) document.head.appendChild(newEl);
    });
  }

  function commit(doc, plan) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active') && typeof drawer.close === 'function') {
      drawer.close();
    }

    syncHead(doc);

    if (plan.backdrop) {
      plan.backdrop.oldStyle.replaceWith(plan.backdrop.newStyle);
      ['data-gc-plate-desktop', 'data-gc-plate-mobile'].forEach(function (name) {
        plan.backdrop.oldEl.setAttribute(name, plan.backdrop.newEl.getAttribute(name) || '');
      });
    }

    var inserted = [];
    plan.levels.forEach(function (level) {
      syncAttributes(level.oldParent, level.newParent);
      replaceAround(level, inserted);
    });

    /* cart-drawer.js binds #cart-icon-bubble once, in its constructor. */
    if (drawer && typeof drawer.setHeaderCartIconAccessibility === 'function') {
      drawer.setHeaderCartIconAccessibility();
    }
    return inserted;
  }

  /* --- after the swap ----------------------------------------------------- */

  function runScripts(nodes) {
    nodes.forEach(function (root) {
      var list = root.tagName === 'SCRIPT' ? [root] : Array.prototype.slice.call(root.querySelectorAll('script'));
      list.forEach(function (inert) {
        var type = (inert.getAttribute('type') || '').toLowerCase();
        if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') return;
        var src = inert.src;
        if (src) {
          if (loadedScripts[src]) return;
          loadedScripts[src] = true;
        }
        var live = document.createElement('script');
        Array.prototype.forEach.call(inert.attributes, function (attr) {
          live.setAttribute(attr.name, attr.value);
        });
        if (src) {
          live.async = false; /* keep document order between new scripts */
        } else {
          live.textContent = inert.textContent;
        }
        inert.parentNode.replaceChild(live, inert);
      });
    });
  }

  function dropBridges(bridges) {
    if (!bridges.length) return;
    var own = Array.prototype.filter.call(main.querySelectorAll('link[rel="stylesheet"]'), function (link) {
      return bridges.some(function (bridge) {
        return bridge.href === link.href;
      });
    });
    Promise.race([Promise.all(own.map(whenLoaded)), timeout(STYLE_WAIT_MS)]).then(function () {
      bridges.forEach(function (bridge) {
        bridge.remove();
      });
    });
  }

  function scrollAfter(url, scroll) {
    if (typeof scroll === 'number') {
      window.scrollTo(0, scroll);
      return;
    }
    var target = null;
    if (url.hash) {
      try {
        target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      } catch (error) {
        target = null;
      }
    }
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }

  function pageView() {
    var events = window.StandardEvents;
    if (!events || typeof events.PageViewEvent !== 'function') return;
    try {
      document.dispatchEvent(
        new events.PageViewEvent({
          page: { template: main.dataset.template || '', title: document.title, url: location.href },
        })
      );
    } catch (error) {
      /* analytics must never break navigation */
    }
  }

  /* --- history ------------------------------------------------------------ */

  function saveScroll() {
    try {
      history.replaceState(Object.assign({}, history.state, { gcScroll: window.scrollY }), '');
    } catch (error) {
      /* Safari rate-limits replaceState; losing one position is harmless */
    }
  }

  var scrollTimer = 0;
  window.addEventListener(
    'scroll',
    function () {
      if (!softened) return;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(saveScroll, 200);
    },
    { passive: true }
  );

  function setBusy(busy) {
    document.documentElement.style.cursor = busy ? 'progress' : '';
    if (busy) main.setAttribute('aria-busy', 'true');
    else main.removeAttribute('aria-busy');
  }

  /* opts.push — a link click (new history entry); otherwise back/forward,
     with opts.scroll the position saved on that entry. */
  function visit(url, opts) {
    var id = ++visitId;
    var href = stripHash(url.href);
    setBusy(true);

    fetchPage(href)
      .then(function (page) {
        if (id !== visitId) return null;
        var doc = new DOMParser().parseFromString(page.html, 'text/html');
        var plan = planSwap(doc);
        if (!plan) throw new Error('gc-navigation: shell mismatch');
        return prepare(doc, plan).then(function (bridges) {
          if (id !== visitId) {
            bridges.forEach(function (bridge) {
              bridge.remove();
            });
            return null;
          }
          var finalUrl = new URL(page.url, location.href);
          finalUrl.hash = url.hash; /* a redirect drops the hash */
          if (opts.push) {
            saveScroll();
            if (stripHash(finalUrl.href) === pageHref) history.replaceState({}, '', finalUrl.href);
            else history.pushState({}, '', finalUrl.href);
            if (!softened) {
              softened = true;
              if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
            }
          }
          var inserted = commit(doc, plan);
          pageHref = stripHash(finalUrl.href);
          scrollAfter(finalUrl, opts.scroll);
          setBusy(false);
          runScripts(inserted);
          dropBridges(bridges);
          document.dispatchEvent(new CustomEvent('gc:page-load'));
          pageView();
          if (opts.push) main.focus({ preventScroll: true });
          return null;
        });
      })
      .catch(function () {
        if (id !== visitId) return;
        /* Anything unexpected: a normal page load (the music stops, as it
           always did). pushState may already have run — assign() then
           simply loads that URL. */
        if (opts.push) location.assign(url.href);
        else location.reload();
      });
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link || !playerLive()) return;
    var url = softUrl(link);
    if (!url) return;
    event.preventDefault();
    visit(url, { push: true });
  });

  window.addEventListener('popstate', function (event) {
    if (!softened) return;
    if (stripHash(location.href) === pageHref) return; /* hash-only step */
    var state = event.state || {};
    visit(new URL(location.href), {
      push: false,
      scroll: typeof state.gcScroll === 'number' ? state.gcScroll : 0,
    });
  });

  /* Prefetch on hover intent / touch start — the fetch then usually lands
     before the click does. */
  var hoverLink = null;
  var hoverTimer = 0;
  document.addEventListener('mouseover', function (event) {
    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (link === hoverLink) return;
    clearTimeout(hoverTimer);
    hoverLink = link;
    if (!link) return;
    hoverTimer = setTimeout(function () {
      prefetch(link);
    }, HOVER_DELAY_MS);
  });
  document.addEventListener(
    'touchstart',
    function (event) {
      var link = event.target.closest ? event.target.closest('a[href]') : null;
      if (link) prefetch(link);
    },
    { passive: true }
  );
})();
